import { usePrevious } from '@sequencehq/utils'
import { FC, useCallback, useEffect, useReducer, useRef } from 'react'
import { AttachmentsEmptyState } from './AttachmentsEmptyState'
import {
  ATTACHMENT_ERROR,
  attachmentReducer,
  isServerAsset,
  MAX_FILE_COUNT,
  noAttachments
} from './attachmentReducer'
import {
  Text,
  GreyGrey80,
  Primary12Regular,
  Primary13Regular,
  Box,
  Flex
} from '@sequencehq/core-components'
import type { ServerAsset } from '@sequencehq/api/utils/commonModels'
import { AttachmentList } from './AttachmentList'
import { match, P } from 'ts-pattern'
import { AttachmentPreview } from './AttachmentPreview'
import { useDropzone } from 'react-dropzone'
import { MAX_FILE_SIZE } from 'Assets/constants.ts'
import { dequal } from 'dequal'

export type AttachmentsProps = {
  initialAttachments?: ServerAsset[]
  onChange?: (assets: ServerAsset[]) => void // called each time an upload completes
  onAllUploadsComplete?: (assets: ServerAsset[]) => void // called when all uploaded files finish uploading
  disableModifyAttachments?: false | { reason: string }
  attachmentToPreview?: ServerAsset
  onSelectedPreviewChange?: (asset: ServerAsset | null) => void
  acceptedMimeTypes: string[]
  maxFileSizeText?: string
  maxFileSize?: number
}

export const Attachments: FC<AttachmentsProps> = ({
  initialAttachments = null,
  onChange,
  onAllUploadsComplete,
  disableModifyAttachments = false,
  attachmentToPreview,
  onSelectedPreviewChange,
  acceptedMimeTypes,
  maxFileSizeText,
  maxFileSize = MAX_FILE_SIZE
}) => {
  const [state, dispatch] = useReducer(attachmentReducer, {
    error: null,
    attachments: initialAttachments,
    attachmentToPreview: null,
    isUploading: false
  })

  const prevIsUploading = usePrevious(state.isUploading)

  const prevInitialAttachments = usePrevious(initialAttachments)

  /**
   * The 'sync' useEffect. We only use this when the attachment inspector
   * is fully controlled (with an onChange and value).
   *
   * If we have reinitialized the attachments, we need to set the state to match it.
   * Otherwise, if our internal state has changed vs. the initial attachments,
   * we need to call the onChange callback.
   */
  useEffect(() => {
    if (!initialAttachments || !onChange) {
      return
    }

    if (!dequal(initialAttachments, prevInitialAttachments)) {
      return dispatch({
        type: 'SET_ATTACHMENTS',
        attachments: initialAttachments
      })
    }

    const serverAssets = state.attachments?.filter(isServerAsset) ?? []

    if (!dequal(serverAssets, initialAttachments)) {
      onChange?.(serverAssets)
    }
  }, [initialAttachments, prevInitialAttachments, state.attachments, onChange])

  useEffect(() => {
    if (prevIsUploading && !state.isUploading) {
      onAllUploadsComplete?.(state.attachments?.filter(isServerAsset) ?? [])
    }
  }, [
    prevIsUploading,
    state.attachments,
    state.isUploading,
    onAllUploadsComplete
  ])

  useEffect(() => {
    if (!attachmentToPreview) {
      return
    }

    dispatch({ type: 'PREVIEW_ATTACHMENT', attachment: attachmentToPreview })
  }, [attachmentToPreview])

  const fileInputRef = useRef<HTMLInputElement>(null)

  const triggerFileInput = () => {
    fileInputRef.current?.click()
  }

  const handleFilesPicked = () => {
    const files = fileInputRef.current?.files
    if (!files) {
      return
    }
    dispatch({ type: 'USER_PICKED_FILES', files: Array.from(files) })
    fileInputRef.current.value = ''
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    dispatch({ type: 'USER_PICKED_FILES', files: Array.from(acceptedFiles) })
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    multiple: true,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxSize: maxFileSize,
    maxFiles: MAX_FILE_COUNT,
    disabled:
      Boolean(state.attachmentToPreview) || Boolean(disableModifyAttachments)
  })

  const containerWidth = state.attachmentToPreview ? '440px' : '320px'

  // callback fired when the selected attachment preview changes
  useEffect(() => {
    onSelectedPreviewChange?.(state.attachmentToPreview)
  }, [state.attachmentToPreview, onSelectedPreviewChange])

  return (
    <Box
      width="fit-content"
      height="100%"
      overflow="auto"
      data-testid="attachments.inspector.content"
      {...getRootProps()}
    >
      <Box width={containerWidth} height="100%">
        {isDragActive ? (
          <Flex
            height="100%"
            width="100%"
            alignItems="center"
            justifyContent="center"
          >
            <Text {...Primary13Regular}>Upload attachments</Text>
          </Flex>
        ) : (
          match(state)
            .when(noAttachments, () => (
              <Box width="320px" height="100%">
                <AttachmentsEmptyState
                  onClickAddAttachment={triggerFileInput}
                  disableModifyAttachments={disableModifyAttachments}
                  maxFileSizeText={maxFileSizeText}
                />
              </Box>
            ))
            .with({ attachmentToPreview: P.not(P.nullish) }, () => (
              <Box height="100%" width="440px">
                <AttachmentPreview
                  state={state}
                  dispatch={dispatch}
                  disableControls={Boolean(disableModifyAttachments)}
                />
              </Box>
            ))
            .otherwise(() => (
              <Box height="100%" p="16px" width="320px">
                <AttachmentList
                  state={state}
                  dispatch={dispatch}
                  onClickAddAttachment={triggerFileInput}
                  disableModifyAttachments={disableModifyAttachments}
                  maxFileSize={maxFileSize}
                />
              </Box>
            ))
        )}
        {state.error && (
          <Text align="center" color={GreyGrey80} {...Primary12Regular}>
            {toUserFriendlyError(state.error)}
          </Text>
        )}
        <input
          type="file"
          style={{ display: 'none' }}
          ref={fileInputRef}
          accept={acceptedMimeTypes.join(',')}
          multiple
          onChange={handleFilesPicked}
        />
        <input {...getInputProps()} />
      </Box>
    </Box>
  )
}

function toUserFriendlyError(error: ATTACHMENT_ERROR) {
  switch (error) {
    case 'MAX_FILE_COUNT_EXCEEDED':
      return `You can only add up to ${MAX_FILE_COUNT} files`
    default:
      return 'An error occurred'
  }
}
